/**
 * Feature Gate Hook — Client Portal
 *
 * Evaluates features for the CLIENT context — returns union of features
 * from all active relationships with prestataires.
 *
 * Uses: GET /api/v1/features/client/evaluate?plan_tier=X
 */

import { useQuery } from '@tanstack/react-query'
import apiClient from '@/lib/api/client'

export interface FeatureEvalResult {
  code: string
  enabled: boolean
  category: 'PUBLIC' | 'PRE_CONTRACT' | 'POST_CONTRACT'
  reason?: string
}

export interface FeatureEvalResponse {
  features: Record<string, FeatureEvalResult>
  context: Record<string, unknown>
}

const FEATURE_ENGINE_URL = import.meta.env.VITE_FEATURE_ENGINE_URL || 'http://localhost:5135'

async function fetchClientFeatureEval(planTier: number): Promise<FeatureEvalResponse> {
  const response = await apiClient.get(`${FEATURE_ENGINE_URL}/api/v1/features/client/evaluate?plan_tier=${planTier}`)
  return response.data
}

export function useFeatureEval(planTier: number = 1) {
  return useQuery<FeatureEvalResponse>({
    queryKey: ['features', 'client-evaluate', planTier],
    queryFn: () => fetchClientFeatureEval(planTier),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 2,
  })
}

export function useFeatureGate(featureCode: string, planTier: number = 1) {
  const { data, isLoading, error } = useFeatureEval(planTier)
  const feature = data?.features?.[featureCode]

  return {
    isEnabled: feature?.enabled ?? false,
    isLoading,
    category: feature?.category ?? 'POST_CONTRACT',
    reason: feature?.reason,
    error,
  }
}

export function useModuleGate(moduleCode: string, planTier: number = 1) {
  const { data, isLoading } = useFeatureEval(planTier)

  if (!data?.features) {
    return { isEnabled: false, isLoading, enabledFeatures: [] as string[] }
  }

  // Additive matching: EXACT feature code (single-feature add-ons like
  // `business_page_builder`) OR any dotted child under the module prefix
  // (multi-feature modules). Exact match added so a lone enabled add-on opens
  // its gate; previously only `startsWith("${moduleCode}.")` matched.
  const moduleFeatures = Object.entries(data.features)
    .filter(([code]) => code === moduleCode || code.startsWith(`${moduleCode}.`))

  const enabledFeatures = moduleFeatures
    .filter(([, feat]) => feat.enabled)
    .map(([code]) => code)

  return {
    isEnabled: enabledFeatures.length > 0,
    isLoading,
    enabledFeatures,
    totalFeatures: moduleFeatures.length,
  }
}
